import { Controller } from "@hotwired/stimulus"
import { showPageFlash } from "../../../../src/application/flash_messages.js"

// Educator Home AI Assistant thread feedback — DS modal UI (shared with VPE visuals),
// submits to /ai_assistant/threads/:id/feedbacks (not VPE case-experience flow).
export default class extends Controller {
  static targets = ["modal", "ratingInput", "issuesInput", "commentInput", "submitButton"]

  static values = {
    threadId: Number,
  }

  connect() {
    this.onEmoticonSelected = this.onEmoticonSelected.bind(this)
    this.onEscape = this.onEscape.bind(this)
    this.element.addEventListener("badge-emoticons:selected", this.onEmoticonSelected)
    // Capture so we close feedback before the parent AI railsui-modal Esc handler.
    window.addEventListener("keyup", this.onEscape, true)
  }

  disconnect() {
    this.element.removeEventListener("badge-emoticons:selected", this.onEmoticonSelected)
    window.removeEventListener("keyup", this.onEscape, true)
  }

  open(event) {
    event?.preventDefault?.()
    if (!this.threadIdValue) return

    this.railsuiModal?.show?.(event)
  }

  cancel(event) {
    event?.preventDefault?.()
    this.closeFeedback(event)
  }

  handleCheckboxKeydown(event) {
    if (event.key !== " " && event.key !== "Enter") return
    event.preventDefault()
    event.currentTarget.checked = !event.currentTarget.checked
    this.updateIssues()
  }

  updateIssues() {
    if (!this.hasIssuesInputTarget) return
    const checked = Array.from(this.element.querySelectorAll(".issue-checkbox:checked")).map(
      (checkbox) => checkbox.value,
    )
    this.issuesInputTarget.value = JSON.stringify(checked)
  }

  updateSubmitButtonState() {
    if (!this.hasSubmitButtonTarget) return
    this.submitButtonTarget.disabled = !this.hasRatingInputTarget || !this.ratingInputTarget.value
  }

  async submit(event) {
    event?.preventDefault?.()
    if (!this.hasSubmitButtonTarget || this.submitButtonTarget.disabled) return
    if (!this.threadIdValue || !this.hasRatingInputTarget) return

    const rating = parseInt(this.ratingInputTarget.value, 10)
    if (Number.isNaN(rating)) return

    let issues = []
    try {
      issues = this.hasIssuesInputTarget && this.issuesInputTarget.value
        ? JSON.parse(this.issuesInputTarget.value)
        : Array.from(this.element.querySelectorAll(".issue-checkbox:checked")).map((cb) => cb.value)
    } catch {
      issues = []
    }

    const comment = this.hasCommentInputTarget ? this.commentInputTarget.value.trim() : ""
    this.submitButtonTarget.disabled = true

    try {
      const response = await fetch(`/ai_assistant/threads/${this.threadIdValue}/feedbacks`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content,
        },
        body: JSON.stringify({
          feedback: {
            smiley_rating: rating,
            issues,
            comment,
          },
        }),
      })

      if (!response.ok) {
        const message = await this.errorMessageFrom(response)
        throw new Error(message)
      }

      this.closeFeedback(event)
      showPageFlash("Thank you for your feedback!", "success")
    } catch (error) {
      console.error(error)
      this.submitButtonTarget.disabled = false
      showPageFlash(error?.message || "Please try again.", "error")
    }
  }

  async errorMessageFrom(response) {
    try {
      const data = await response.json()
      const errors = data?.errors
      if (Array.isArray(errors) && errors.length) return errors.join(", ")
      if (typeof errors === "string" && errors) return errors
      if (typeof data?.error === "string" && data.error) return data.error
    } catch {
      // ignore non-JSON bodies
    }
    return "Please try again."
  }

  onEmoticonSelected(event) {
    const input = event.detail?.input
    if (!(input instanceof HTMLInputElement)) return

    const rating = parseInt(this.ratingParamFrom(input), 10)
    if (Number.isNaN(rating) || !this.hasRatingInputTarget) return

    this.ratingInputTarget.value = String(rating)
    this.updateSubmitButtonState()
  }

  onEscape(event) {
    if (event.key !== "Escape" && event.keyCode !== 27) return
    if (!this.isFeedbackOpen) return

    event.preventDefault()
    event.stopImmediatePropagation()
    event._railsuiModalEscapeHandled = true
    this.closeFeedback(event)
  }

  closeFeedback(event) {
    this.resetForm()
    this.railsuiModal?.close?.(event)
  }

  resetForm() {
    if (this.hasRatingInputTarget) this.ratingInputTarget.value = ""
    if (this.hasIssuesInputTarget) this.issuesInputTarget.value = ""
    if (this.hasCommentInputTarget) this.commentInputTarget.value = ""

    this.element.querySelectorAll(".issue-checkbox:checked").forEach((checkbox) => {
      checkbox.checked = false
    })

    this.element.querySelectorAll(".badge-aq-emoticon-input[type='radio']").forEach((input) => {
      input.checked = false
      const label = input.labels?.[0] || input.closest("label") || input.nextElementSibling
      if (label instanceof HTMLElement) {
        label.classList.remove("badge-aq-emoticon--checked")
      }
    })

    this.updateSubmitButtonState()
  }

  get isFeedbackOpen() {
    if (!this.hasModalTarget) return false
    const container = this.modalTarget.querySelector("[data-railsui-modal-target='container']")
    return Boolean(container && !container.classList.contains("hidden"))
  }

  ratingParamFrom(input) {
    return (
      input.getAttribute("data-educator-home-page--ai-assistant-feedback-rating-param") ||
      input.dataset.educatorHomePageAiAssistantFeedbackRatingParam
    )
  }

  get railsuiModal() {
    if (!this.hasModalTarget) return null
    return this.application.getControllerForElementAndIdentifier(this.modalTarget, "railsui-modal")
  }
}
